import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/shared/SEOHead';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { RotateCcw, Hammer } from 'lucide-react';
import { useDataStandards } from '@/hooks/useDataStandards';
import { FieldSelector, CodePreview } from '@/components/builder';
import { DataStandard, SchemaField } from '@/lib/data-standards/schemas';
import { useIsMobile } from '@/hooks/use-mobile';
import { Skeleton } from '@/components/ui/skeleton';

function BuilderSkeleton({ isMobile }: { isMobile: boolean }) {
  return (
    <div className="space-y-6">
      {/* Standard Selector Skeleton */}
      <div>
        <Skeleton className="h-4 w-32 mb-2" />
        <Skeleton className="h-10 w-full sm:w-80" />
      </div>

      {/* Content Skeleton */}
      {isMobile ? (
        <div className="border rounded-lg bg-card overflow-hidden">
          <Skeleton className="h-10 w-full" />
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-5 w-5 rounded" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-0 border rounded-lg bg-card overflow-hidden min-h-[70vh]">
          {/* Fields panel skeleton */}
          <div className="border-r p-4 space-y-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-8 w-20" />
            </div>
            <Skeleton className="h-9 w-full" />
            <div className="space-y-2">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="flex items-center gap-3 p-2">
                  <Skeleton className="h-5 w-5 rounded" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3 w-40" />
                  </div>
                  <Skeleton className="h-5 w-14 rounded-full" />
                </div>
              ))}
            </div>
          </div>
          {/* Preview panel skeleton */}
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-5 w-32" />
              <div className="flex gap-2">
                <Skeleton className="h-8 w-8" />
                <Skeleton className="h-8 w-8" />
              </div>
            </div>
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-96 w-full rounded-lg" />
          </div>
        </div>
      )}
    </div>
  );
}

export default function DataStandardsBuilder() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { schemas, isLoading } = useDataStandards();
  const isMobile = useIsMobile();

  const [selectedStandard, setSelectedStandard] = useState<DataStandard | null>(null);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [requiredOverrides, setRequiredOverrides] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState<'fields' | 'preview'>('fields');

  // Extract fields from schema
  const schemaFields = useMemo(() => {
    if (!selectedStandard) return [];

    // Use the fields array from the standard if available
    if (selectedStandard.fields && selectedStandard.fields.length > 0) {
      return selectedStandard.fields;
    }

    // Fallback: extract from schema properties
    const schemaObj = selectedStandard.schema as Record<string, unknown>;
    const properties = (schemaObj.properties || {}) as Record<string, Record<string, unknown>>;

    return Object.entries(properties).map(([name, prop]): SchemaField => ({
      name,
      type: (prop.type as string) || 'unknown',
      description: (prop.description as string) || '',
      required: false,
    }));
  }, [selectedStandard]);

  // Load from URL params on mount
  useEffect(() => {
    if (schemas.length === 0) return;

    const standardParam = searchParams.get('standard');
    const fieldsParam = searchParams.get('fields');

    if (standardParam) {
      const standard = schemas.find(s => s.id === standardParam);
      if (standard) {
        setSelectedStandard(standard);
        if (fieldsParam) {
          const fields = fieldsParam.split(',').filter(Boolean);
          setSelectedFields(fields);
        }
      }
    }
  }, [schemas, searchParams]);

  // Update URL when selections change
  useEffect(() => {
    if (!selectedStandard) {
      setSearchParams({});
      return;
    }

    const params = new URLSearchParams();
    params.set('standard', selectedStandard.id);
    if (selectedFields.length > 0) {
      params.set('fields', selectedFields.join(','));
    }
    setSearchParams(params, { replace: true });
  }, [selectedStandard, selectedFields, setSearchParams]);

  const handleStandardChange = (standardId: string) => {
    const standard = schemas.find(s => s.id === standardId);
    if (standard) {
      setSelectedStandard(standard);
      setSelectedFields([]);
      setRequiredOverrides({});
    }
  };

  const handleRequiredChange = (fieldName: string, required: boolean) => {
    setRequiredOverrides(prev => ({
      ...prev,
      [fieldName]: required,
    }));
  };

  const handleReset = () => {
    setSelectedFields([]);
    setRequiredOverrides({});
  };

  return (
    <Layout>
      <SEOHead path="/api-standards/builder" />

      <div className="container py-6">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/api-standards">API Standards</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Schema Builder</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Hammer className="h-6 w-6 sm:h-7 sm:w-7 text-primary" />
              Schema Builder
            </h1>
            <p className="text-muted-foreground mt-1">
              Create custom schemas from our data standards
            </p>
          </div>
          {selectedStandard && !isLoading && (
            <Button variant="outline" onClick={handleReset} className="gap-2">
              <RotateCcw className="h-4 w-4" />
              Reset
            </Button>
          )}
        </div>

        {isLoading ? (
          <BuilderSkeleton isMobile={isMobile} />
        ) : (
          <>
            {/* Standard Selector */}
            <div className="mb-6">
              <label className="text-sm font-medium mb-2 block">Select a Standard</label>
              <Select
                value={selectedStandard?.id || ''}
                onValueChange={handleStandardChange}
              >
                <SelectTrigger className="w-full sm:w-80">
                  <SelectValue placeholder="Choose a data standard..." />
                </SelectTrigger>
                <SelectContent>
                  {schemas.map(schema => (
                    <SelectItem key={schema.id} value={schema.id}>
                      {schema.title} v{schema.version}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Main Content */}
            {selectedStandard ? (
              isMobile ? (
                // Mobile: Tabbed layout
                <div className="border rounded-lg bg-card overflow-hidden">
                  <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'fields' | 'preview')}>
                    <TabsList className="w-full rounded-none border-b">
                      <TabsTrigger value="fields" className="flex-1">
                        Fields ({selectedFields.length})
                      </TabsTrigger>
                      <TabsTrigger value="preview" className="flex-1">
                        Preview
                      </TabsTrigger>
                    </TabsList>
                    <TabsContent value="fields" className="m-0 h-[60vh]">
                      <FieldSelector
                        fields={schemaFields}
                        schema={selectedStandard.schema}
                        selectedFields={selectedFields}
                        onSelectionChange={setSelectedFields}
                        requiredOverrides={requiredOverrides}
                        onRequiredChange={handleRequiredChange}
                      />
                    </TabsContent>
                    <TabsContent value="preview" className="m-0 h-[60vh]">
                      <CodePreview
                        schema={selectedStandard.schema}
                        fields={schemaFields}
                        selectedFields={selectedFields}
                        requiredOverrides={requiredOverrides}
                        standardName={selectedStandard.title}
                        standardVersion={selectedStandard.version}
                        standardSlug={selectedStandard.id}
                      />
                    </TabsContent>
                  </Tabs>
                </div>
              ) : (
                // Desktop: Side-by-side layout
                <div className="grid grid-cols-2 gap-0 border rounded-lg bg-card overflow-hidden min-h-[70vh]">
                  <FieldSelector
                    fields={schemaFields}
                    schema={selectedStandard.schema}
                    selectedFields={selectedFields}
                    onSelectionChange={setSelectedFields}
                    requiredOverrides={requiredOverrides}
                    onRequiredChange={handleRequiredChange}
                  />
                  <CodePreview
                    schema={selectedStandard.schema}
                    fields={schemaFields}
                    selectedFields={selectedFields}
                    requiredOverrides={requiredOverrides}
                    standardName={selectedStandard.title}
                    standardVersion={selectedStandard.version}
                    standardSlug={selectedStandard.id}
                  />
                </div>
              )
            ) : (
              // Empty state
              <div className="border rounded-lg bg-card p-12 text-center">
                <Hammer className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Select a Standard to Begin</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Choose a data standard from the dropdown above to start building your custom schema.
                  You can select which fields to include and customize required status.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
